import {
  BoundNumberField,
  CollapseToggle,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  Icon,
  numericColumn,
  simpleHeader,
  useTestIds,
} from "@homebound/beam";
import { ObjectConfig, ObjectState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useMemo } from "react";
import { DesignPackageLocation, SaveCostCodeInput, SaveItemInput } from "src/generated/graphqlTypes";
import { FinishScheduleCostCodeFragment } from "src/routes/admin/finishSchedule/FinishScheduleAdmin.generated";
import { reorderRows } from "src/routes/libraries/design-catalog/personalization-option/utils";
import { adminFinishScheduleCostCodeRoute } from "src/routing/domains/adminPaths";

export type FinishScheduleItem = FinishScheduleCostCodeFragment["items"][number];

export type FinishScheduleCostCodeForm = Pick<SaveCostCodeInput, "id" | "finishScheduleOrder">;

export type FinishScheduleItemForm = Pick<SaveItemInput, "id" | "finishScheduleOrder">;

type GetCostCodeFormState = (cc: FinishScheduleCostCodeFragment) => ObjectState<FinishScheduleCostCodeForm>;

type GetItemFormState = (item: FinishScheduleItem) => ObjectState<FinishScheduleItemForm>;

type FinishScheduleCostCodeTableProps = {
  designPackageLocation: DesignPackageLocation;
  costCodes: FinishScheduleCostCodeFragment[];
  getFormState: GetCostCodeFormState;
  getItemFormState: GetItemFormState;
};

export function FinishScheduleCostCodeTable(props: FinishScheduleCostCodeTableProps) {
  const { designPackageLocation, costCodes, getFormState, getItemFormState } = props;
  const tid = useTestIds(props, "finishScheduleCostCodeTable");

  const columns = useMemo(
    () => createColumns(tid, designPackageLocation, costCodes, getFormState, getItemFormState),
    [tid, designPackageLocation, costCodes, getFormState, getItemFormState],
  );

  return (
    <>
      <Observer>
        {() => (
          <GridTable
            {...tid}
            style={{ bordered: true, allWhite: true }}
            columns={columns}
            rows={createRows(designPackageLocation, costCodes, getFormState, getItemFormState)}
          />
        )}
      </Observer>
      <div css={Css.sm.gray700.pt3.pb4.$}>
        A standard legal disclaimer is automatically included at the bottom of each finish schedule.
      </div>
    </>
  );
}

/** Sort key for finish schedule order; nulls/undefined sort to the end. */
function orderKey(order: number | null | undefined): number {
  return order ?? Number.MAX_SAFE_INTEGER;
}

function costCodeNameLabel(cc: FinishScheduleCostCodeFragment, designPackageLocation: DesignPackageLocation): string {
  const customLabel =
    designPackageLocation === DesignPackageLocation.Exterior
      ? cc.exteriorFinishScheduleLabel
      : cc.interiorFinishScheduleLabel;
  return customLabel ?? cc.name;
}

type LockedRowData = { name: string };

type ItemRowData = { item: FinishScheduleItem; costCode: FinishScheduleCostCodeFragment; costCodeName: string };

type CustomItemRowData = { name: string; costCodeName: string };

type Row =
  | { kind: "header"; data: undefined }
  | { kind: "locked"; id: string; data: LockedRowData }
  | { kind: "costCode"; id: string; data: FinishScheduleCostCodeFragment }
  | { kind: "item"; id: string; data: ItemRowData }
  | { kind: "customItem"; id: string; data: CustomItemRowData };

const lockedRows: LockedRowData[] = [{ name: "Project Summary" }, { name: "Selected Options" }];

function createColumns(
  tid: ReturnType<typeof useTestIds>,
  designPackageLocation: DesignPackageLocation,
  costCodes: FinishScheduleCostCodeFragment[],
  getFormState: GetCostCodeFormState,
  getItemFormState: GetItemFormState,
): GridColumn<Row>[] {
  return [
    column<Row>({
      header: (_data, { row }) => <CollapseToggle row={row} />,
      locked: emptyCell,
      costCode: (_data, { row }) => <CollapseToggle row={row} />,
      item: emptyCell,
      customItem: emptyCell,
      w: "36px",
    }),
    numericColumn<Row>({
      header: "Order",
      // TODO(APP-1578): swap to the closed `lock` icon once Beam ships it.
      locked: () => <Icon icon="lockOpen" inc={2} tooltip="This row is always included and can't be reordered." />,
      costCode: (cc) => (
        <BoundNumberField
          compact
          labelStyle="hidden"
          label="Order"
          field={getFormState(cc).finishScheduleOrder}
          onBlur={() => renumberOrders(costCodesInLocation(designPackageLocation, costCodes).map(getFormState), cc.id)}
          {...tid.costCodeOrder}
        />
      ),
      item: ({ item, costCode }) => (
        <BoundNumberField
          compact
          labelStyle="hidden"
          label="Order"
          field={getItemFormState(item).finishScheduleOrder}
          onBlur={() => renumberOrders(itemsInLocation(costCode, designPackageLocation).map(getItemFormState), item.id)}
          {...tid.itemOrder}
        />
      ),
      customItem: () => <Icon icon="lockOpen" inc={2} tooltip="This item is always included and can't be reordered." />,
      w: "72px",
    }),
    column<Row>({
      header: "Name",
      locked: ({ name }) => name,
      costCode: (cc) => ({
        content: costCodeNameLabel(cc, designPackageLocation),
        typeScale: "xsSb",
        // Pass the current tab's location so the detail page edits that section (a cost code can have
        // both interior and exterior items, each its own section).
        onClick: adminFinishScheduleCostCodeRoute.url({ costCodeId: cc.id }, { location: designPackageLocation }),
      }),
      item: ({ item }) => ({ content: item.name, indent: 1 }),
      customItem: ({ name }) => ({ content: name, indent: 1 }),
      mw: "160px",
    }),
    column<Row>({
      header: "Cost Code Group",
      locked: () => "-",
      costCode: emptyCell,
      item: ({ costCodeName }) => costCodeName,
      customItem: ({ costCodeName }) => costCodeName,
      w: "180px",
    }),
  ];
}

function itemInLocation(item: FinishScheduleItem, designPackageLocation: DesignPackageLocation): boolean {
  // Only items with material attributes appear in the finish schedule (matches the detail page).
  return item.designPackageLocation.code === designPackageLocation && item.materialAttributeDimensions.length > 0;
}

/** Cost codes pulled into a *different* cost code as custom config items (e.g. Cabinet Hardware under Cabinets); hidden from the top level since they only show nested. */
function customConfigCostCodeIds(costCodes: FinishScheduleCostCodeFragment[]): Set<string> {
  return new Set(
    costCodes.flatMap((cc) =>
      cc.finishScheduleChildItems.map((item) => item.costCode.id).filter((childId) => childId !== cc.id),
    ),
  );
}

/** Cost codes with at least one item in the given DP location, minus any hidden as a custom config item. */
function costCodesInLocation(
  designPackageLocation: DesignPackageLocation,
  costCodes: FinishScheduleCostCodeFragment[],
): FinishScheduleCostCodeFragment[] {
  const hidden = customConfigCostCodeIds(costCodes);
  return costCodes.filter(
    (cc) => !hidden.has(cc.id) && cc.items.some((item) => itemInLocation(item, designPackageLocation)),
  );
}

/** Items of a cost code that belong to the given DP location (mixed cost codes only show matching items). */
function itemsInLocation(cc: FinishScheduleCostCodeFragment, designPackageLocation: DesignPackageLocation) {
  return cc.items.filter((item) => itemInLocation(item, designPackageLocation));
}

/** A cost code's custom config items in the DP location — hardcoded by the backend, so no attribute check; skips any already shown as an own item (e.g. Tile's TL-*). */
function customItemsInLocation(cc: FinishScheduleCostCodeFragment, designPackageLocation: DesignPackageLocation) {
  const ownIds = new Set(cc.items.map((item) => item.id));
  return cc.finishScheduleChildItems.filter(
    (item) => !ownIds.has(item.id) && item.designPackageLocation.code === designPackageLocation,
  );
}

function createRows(
  designPackageLocation: DesignPackageLocation,
  costCodes: FinishScheduleCostCodeFragment[],
  getFormState: GetCostCodeFormState,
  getItemFormState: GetItemFormState,
): GridDataRow<Row>[] {
  // Order by the live form value (so live edits re-sort), nulls last. We sort manually rather than via
  // GridTable so the two hardcoded locked rows always stay pinned at the top.
  const sortedCostCodes = costCodesInLocation(designPackageLocation, costCodes).sort(
    (a, b) => orderKey(getFormState(a).finishScheduleOrder.value) - orderKey(getFormState(b).finishScheduleOrder.value),
  );

  return [
    simpleHeader,
    ...lockedRows.map((data) => ({ kind: "locked" as const, id: `locked-${data.name}`, data })),
    ...sortedCostCodes.map((cc) => ({
      kind: "costCode" as const,
      id: cc.id,
      data: cc,
      children: [
        ...itemsInLocation(cc, designPackageLocation)
          .sort(
            (a, b) =>
              orderKey(getItemFormState(a).finishScheduleOrder.value) -
              orderKey(getItemFormState(b).finishScheduleOrder.value),
          )
          .map((item) => ({
            kind: "item" as const,
            id: item.id,
            data: { item, costCode: cc, costCodeName: costCodeNameLabel(cc, designPackageLocation) },
          })),
        // Custom config items are pinned after the cost code's own items, in the backend's fixed order.
        // Parent-scoped id since one item can nest under multiple parents (e.g. Cabinet Hardware).
        ...customItemsInLocation(cc, designPackageLocation).map((item) => ({
          kind: "customItem" as const,
          id: `${cc.id}-${item.id}`,
          data: { name: item.name, costCodeName: costCodeNameLabel(cc, designPackageLocation) },
        })),
      ],
    })),
  ];
}

/**
 * After a row's Order is edited, move it to the typed position and renumber its siblings' orders to
 * a contiguous 1..n (no duplicates/gaps) — used for both cost codes within a section and items
 * within a cost code. Mirrors the personalization-option wizard.
 */
function renumberOrders(forms: ObjectState<FinishScheduleCostCodeForm | FinishScheduleItemForm>[], editedId: string) {
  const updated = reorderRows(
    forms.map((os) => ({ id: os.id.value!, order: os.finishScheduleOrder.value })),
    editedId,
  );
  const orderById = new Map(updated.map((u) => [u.id, u.order]));
  forms.forEach((os) => os.finishScheduleOrder.set(orderById.get(os.id.value!) ?? null));
}

export const finishScheduleCostCodeConfig: ObjectConfig<FinishScheduleCostCodeForm> = {
  id: { type: "value" },
  finishScheduleOrder: { type: "value" },
};

export const finishScheduleItemConfig: ObjectConfig<FinishScheduleItemForm> = {
  id: { type: "value" },
  finishScheduleOrder: { type: "value" },
};
